import { FC, PropsWithChildren, useState } from 'react'
import {
  Text,
  BorderRadius6,
  GreyWhite,
  IndigoIndigo50,
  Primary13Regular,
  IconArrowOutOfBox,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useDropzone } from 'react-dropzone'
import { useIsAutomationActive } from 'Automations/hooks/useIsAutomationActive'
import { useUploadAssets } from 'Assets/hooks/useUploadAssets'
import { validatePickedFiles } from 'Assets/utils'
import {
  ACCEPT_CONTRACT_MIME_TYPE,
  MAX_CONTRACTS_PER_UPLOAD
} from './constants'
import { NoContractAutomationModal } from './noContractAutomation.modal'
import { useIsGlobalDropzoneDisabled } from './useIsGlobalDropzoneDisabled'
import { useContractUpload } from './useContractUpload'

export const GlobalContractDropzone: FC<PropsWithChildren> = ({ children }) => {
  const { isActive: isAutomationActive } = useIsAutomationActive({
    triggerType: 'CONTRACT_UPLOADED'
  })
  const {
    onFilesPicked,
    onUploadSuccess,
    onUploadFailure,
    onAllUploadsComplete
  } = useContractUpload()
  const uploadAssets = useUploadAssets()

  const [showNoWorkflowModal, setShowNoWorkflowModal] = useState(false)
  const isDropzoneDisabled = useIsGlobalDropzoneDisabled()

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: isDropzoneDisabled,
    onDrop: async (files: File[]) => {
      const pdfFiles = files.filter(
        file => file.type === ACCEPT_CONTRACT_MIME_TYPE
      )

      if (pdfFiles.length === 0) {
        if (files.length > 0) {
          onUploadFailure('Only PDF files can be uploaded')
        }
        return
      }

      if (!isAutomationActive) {
        setShowNoWorkflowModal(true)
        return
      }

      const validationError = validatePickedFiles({
        files: pdfFiles,
        maximumNumberOfFiles: MAX_CONTRACTS_PER_UPLOAD
      })

      if (validationError) {
        onUploadFailure(validationError)
        return
      }

      onFilesPicked(pdfFiles)
      try {
        await uploadAssets({
          files: pdfFiles,
          onUploadSuccess,
          onUploadFailure
        })
      } finally {
        onAllUploadsComplete()
      }
    },
    noClick: true,
    multiple: true
  })

  return (
    <Box
      {...getRootProps()}
      position="relative"
      width="100%"
      height="100%"
      outline="none"
    >
      <input
        {...getInputProps()}
        data-testid="globalContractDropzone.fileInput"
      />
      {children}
      {isDragActive && (
        <Center
          position="absolute"
          inset="0"
          pointerEvents="none"
          backgroundColor="rgba(99, 102, 241, 0.06)"
          borderLeft={`${IndigoIndigo50} 1px solid`}
          zIndex={9999}
          data-testid="globalContractDropzone.overlay"
        >
          <Flex
            alignItems="center"
            gap="10px"
            backgroundColor={IndigoIndigo50}
            borderRadius={BorderRadius6}
            paddingX="16px"
            paddingY="12px"
          >
            <IconArrowOutOfBox color={GreyWhite} />
            <Text {...Primary13Regular} color={GreyWhite}>
              Drop files to upload them to Watchtower
            </Text>
          </Flex>
        </Center>
      )}
      <NoContractAutomationModal
        isOpen={showNoWorkflowModal}
        onClose={() => setShowNoWorkflowModal(false)}
      />
    </Box>
  )
}
